import { settingApi } from '../../../slice/settingApi';

import type { listParamsType } from '../../../slice/types';

export type CustomFieldType = {
  id?: string;
  module?: string;
  label?: string;
  description?: string;
  type?: string;
  options?: string[];
  validation?: {
    minLength?: number | null;
    maxLength?: number | null;
    pattern?: string | null;
  };
  isDisabled?: boolean;
  isRequired?: boolean;
  isVisible?: boolean;
  isBoolean?: boolean;
  placeholder?: string | null;
  defaultValue?: any;
  createdById?: string;
  modifiedById?: string;
};

export interface IFieldItem extends CustomFieldType {
  name?: string;
  createdAt?: string;
  updatedAt?: string;
  isRequired: boolean;
  isVisible: boolean;
  isDisabled: boolean;
  isInbox: boolean;
  moduleId?: string;
}

const CUSTOM_FIELD_API = {
  list: 'api/custom-fields/list',
  add: 'api/custom-fields',
  update: 'api/custom-fields',
  delete: 'api/custom-fields',
};

export const customFieldApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    // CUSTOM FIELDS
    filterCustomFields: builder.mutation({
      query: ({ filters, globalSearch, page = 1, limit = 10, module }: listParamsType) => ({
        url: CUSTOM_FIELD_API.list,
        method: 'POST',
        body: { filters, globalSearch, page, limit, module },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: {
          dynamicfields?: IFieldItem[];
          id: string;
          name: string;
          total?: number;
          totalPages?: number;
        };
      }) => {
        const fieldsList = response?.data?.dynamicfields?.map((field) => ({
          id: field?.id ?? '',
          name: field?.name || field?.id || '',
          label: field?.label ?? '',
          description: field?.description ?? '',
          type: field?.type ?? '',
          validation: {
            minLength: field?.validation?.minLength ?? null,
            maxLength: field?.validation?.maxLength ?? null,
            pattern: field?.validation?.pattern ?? '',
          },
          options: field?.options ?? [],
          isRequired: field?.isRequired ?? false,
          isVisible: field?.isVisible ?? false,
          isDisabled: field?.isDisabled ?? false,
          isBoolean: field?.isBoolean ?? false,
          isInbox: field?.isInbox ?? false,
          placeholder: field?.placeholder ?? null,
          defaultValue: field?.defaultValue ?? null,
          module: response?.data?.name ?? '',
          moduleId: field?.moduleId || response?.data?.id || '',
          createdById: field?.createdById ?? '',
          modifiedById: field?.modifiedById ?? '',
          createdAt: field?.createdAt ?? '',
          updatedAt: field?.updatedAt ?? '',
        }));
        return { list: fieldsList ?? [], total: response?.data?.total ?? 0 };
      },
    }),
    createCustomField: builder.mutation({
      query: ({ ...fields }: CustomFieldType) => ({
        url: CUSTOM_FIELD_API.add,
        method: 'POST',
        body: fields,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    updateCustomField: builder.mutation({
      query: ({ id, updates }: { id: string; updates: Partial<CustomFieldType> }) => ({
        url: `${CUSTOM_FIELD_API.update}/${id}`,
        method: 'PUT',
        body: { id, ...updates },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    deleteCustomFields: builder.mutation({
      query: (ids: string[]) => ({
        url: CUSTOM_FIELD_API.delete,
        method: 'DELETE',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterCustomFieldsMutation,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldsMutation,
} = customFieldApi;
